import {
  Box,
  Card,
  Flex,
  Heading,
  Text,
  colors,
} from '@weareberyl/design-system'
import { Descriptions, message } from 'antd'

import Loading from '~/components/Loading'
import Dropdown from '~/components/shared/Dropdown'
import TabTitle from '~/components/shared/TabTitle'
import {
  ZoneSummaryQuery_zone_Zone,
  ZoneType,
  useZoneSummaryQuery,
} from '~/gql/generated/graphql'
import {
  VehicleIcon,
  capitalize,
  getPositionLinkFromCoordinates,
  isDockedScheme,
  isPromotionalZoneScheme,
} from '~/utils'

import { SetPromotional } from './Actions/SetPromotional'
import { SuspendZone } from './Actions/SuspendZone'
import { ViewHistory } from './Actions/ViewHistory'
import ZoneNotes from './ZoneNotes'
import ZoneTypeStatus from './ZoneTypeStatus'
import useIsAnyLoading, { SetAnyLoading } from './useIsAnyLoading'
import { CurrentSuspensionInfo } from './CurrentSuspensionInfo'
import { ScheduledIcon } from './ScheduledIcon'
import { navigateToServiceNowAssets } from '~/components/Module/context'
import { zoneIdToAssetTag } from '~/components/Job/utils'
import { MenuOutlined } from '@ant-design/icons'
import ServiceNowLogo from '~/components/ServiceNow/ServiceNowLogo'
import { SetPriority } from './Actions/SetPriority'
import ZoneContents from './ZoneContents'
import { sentenceCase } from 'sentence-case'
import { helperText } from '../Accessibility/helperText'

const getHiringOrParkingString = (status: string | undefined) =>
  status ? capitalize(status.split('_')[0]) : undefined

export const ZoneHeading = ({
  name,
  zoneType,
  hasScheduled = false,
  isSuspended = false,
}: {
  name: string
  zoneType: ZoneType
  hasScheduled?: boolean
  isSuspended?: boolean
}) => {
  return (
    <Flex flexDirection="row" flexWrap="nowrap">
      <ZoneTypeStatus zoneType={zoneType} isSuspended={isSuspended} />
      {hasScheduled && (
        <Flex ml={2}>
          <ScheduledIcon />
        </Flex>
      )}
      <Heading variant="h1" px={2}>
        {name ?? 'Unknown'}
      </Heading>
    </Flex>
  )
}

const SectionHeader = ({
  isPromotional,
  isSuspended,
  isPriority,
  schemeId,
  setAnyLoading,
  showPromotional,
  zone,
  ...props
}: {
  isPromotional: boolean
  isSuspended: boolean
  isPriority: boolean
  schemeId: string
  setAnyLoading: SetAnyLoading
  showPromotional: boolean
  zone: ZoneSummaryQuery_zone_Zone
}) => {
  const id = zone.id
  const zoneType = zone?.zone_type ?? ZoneType.normal
  const hasScheduled = (zone?.scheduled_suspensions?.nodes?.length ?? 0) > 0

  return (
    <Flex
      justifyContent="space-between"
      alignItems="baseline"
      width="100%"
      gridGap={1}
      {...props}
    >
      <Box flex="1">
        <ZoneHeading
          zoneType={zoneType}
          name={zone.name}
          hasScheduled={hasScheduled}
          isSuspended={isSuspended}
        />
      </Box>
      <Box bg={colors.white}>
        <Dropdown
          menu={{
            items: [
              {
                icon: <ServiceNowLogo height={15} />,
                onClick: () =>
                  navigateToServiceNowAssets({
                    assetTag: zoneIdToAssetTag(zone.id),
                  }),
                label: (
                  <Text
                    variant="grape"
                    size="small"
                    fontFamily="Hellix-SemiBold"
                  >
                    View in ServiceNow
                  </Text>
                ),
                key: 'view-zone-in-snow',
              },
              {
                label: (
                  <SuspendZone
                    id={id}
                    isSuspended={isSuspended}
                    setAnyLoading={setAnyLoading}
                  />
                ),
                key: 'suspend-zone-with-reason',
              },
              {
                label: showPromotional ? (
                  <SetPromotional
                    id={id}
                    isPromotional={isPromotional}
                    setAnyLoading={setAnyLoading}
                  />
                ) : null,
                key: 'set-promotional',
              },
              {
                label: (
                  <SetPriority
                    id={id}
                    isPriority={isPriority}
                    setAnyLoading={setAnyLoading}
                  />
                ),
                key: 'set-priority',
              },
              {
                label: schemeId ? (
                  <ViewHistory id={id} schemeId={schemeId} />
                ) : null,
                key: 'view-history',
              },
            ].filter(item => item.label !== null),
          }}
        >
          <MenuOutlined />
        </Dropdown>
      </Box>
    </Flex>
  )
}

const SectionStatus = ({
  isAnyLoading,
  isPromotional,
  isSuspended,
  isPriority,
}: {
  isAnyLoading: boolean
  isPromotional: boolean
  isSuspended: boolean
  isPriority: boolean
}) => {
  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Flex gridGap={16}>
        <TabTitle
          tabText={isSuspended ? 'Suspended' : 'Enabled'}
          icon={isSuspended ? 'cross' : 'tick'}
          hoverText={
            isSuspended ? helperText['suspendedBay'] : helperText['enabledBay']
          }
        />
        {isPromotional && (
          <TabTitle
            tabText="Promotional"
            icon="star"
            hoverText={helperText['promotionalBay']}
          />
        )}
        {isPriority && (
          <TabTitle
            tabText="Priority"
            icon="sparkle"
            hoverText={helperText['priorityBay']}
          />
        )}
      </Flex>

      {isAnyLoading && (
        <Box>
          <Loading />
        </Box>
      )}
    </Flex>
  )
}

const ZoneInfo = ({ zone }: { zone: ZoneSummaryQuery_zone_Zone }) => (
  <Descriptions
    column={1}
    size="small"
    style={{ marginTop: 10, marginBottom: 10 }}
  >
    <Descriptions.Item label="Permitted vehicles">
      <Flex alignItems="center" justifyContent="center" style={{ gap: 1 }}>
        {zone?.permitted_vehicles.map(vehicle => (
          <VehicleIcon
            key={vehicle}
            vehicle={vehicle}
            size={21}
            hoverText={sentenceCase(vehicle)}
          />
        ))}
      </Flex>
    </Descriptions.Item>
    <Descriptions.Item label="Centre">
      {getPositionLinkFromCoordinates(zone?.centre?.coordinates)}
    </Descriptions.Item>
    <Descriptions.Item label="Capacity">{zone?.capacity}</Descriptions.Item>
    {isDockedScheme(zone?.scheme?.id) && (
      <Descriptions.Item label="Docked vehicle count">
        {zone?.docked_vehicles_count}
      </Descriptions.Item>
    )}
    <Descriptions.Item label="Hiring status">
      {getHiringOrParkingString(zone?.hiring_capacity_status)}
    </Descriptions.Item>
    <Descriptions.Item label="Parking status">
      {getHiringOrParkingString(zone?.parking_capacity_status)}
    </Descriptions.Item>
  </Descriptions>
)

const Summary = ({ id }) => {
  const { data, error, loading } = useZoneSummaryQuery({ variables: { id } })
  const [isAnyLoading, setAnyLoading] = useIsAnyLoading()

  if (loading) {
    return <Loading />
  }

  if (error) {
    message.error(error.message)
    return null
  }

  const zone = data?.zone
  const schemeId = zone?.scheme?.id ?? null
  const isSuspended = zone?.is_suspended ?? false
  const currentSuspension = zone?.current_suspension ?? undefined
  const showPromotional = Boolean(schemeId && isPromotionalZoneScheme(schemeId))
  // Never mark a bay as promotional in schemes that have showPromotional = false
  const isPromotional = Boolean(zone?.is_promotional && showPromotional)
  const isPriority = zone?.is_priority ?? false

  if (!zone || !schemeId) return null

  return (
    <Card id="zone-summary-card" mb={3} p={5}>
      <Flex flexDirection="column" gridGap={2}>
        <SectionHeader
          isPromotional={isPromotional}
          isSuspended={isSuspended}
          isPriority={isPriority}
          schemeId={schemeId}
          setAnyLoading={setAnyLoading}
          showPromotional={showPromotional}
          zone={zone}
        />
        <SectionStatus
          isAnyLoading={isAnyLoading}
          isPromotional={isPromotional}
          isSuspended={isSuspended}
          isPriority={isPriority}
        />
        {currentSuspension && currentSuspension?.id && (
          <CurrentSuspensionInfo {...currentSuspension} />
        )}
        <Box mt={1}>
          <ZoneNotes zone={zone} />
        </Box>
        <ZoneInfo zone={zone} />
        <ZoneContents zone={zone} />
      </Flex>
    </Card>
  )
}

export default Summary
